// utils/postLoginRedirect.ts

const placeholderBase = "https://internal-redirect.invalid";

/** Matches a whole path segment that is a UUID, i.e. the `[practiceId]` route prefix. */
const practiceIdPathSegmentRegex =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Guards against open redirects: only same-origin relative paths are allowed, and never back to
 * the sign-in page itself (which would create a redirect loop).
 */
export function isSafeInternalRedirectPath(path: string | null | undefined): path is string {
  if (!path || !path.startsWith("/")) {
    return false;
  }

  let url: URL;
  try {
    url = new URL(path, placeholderBase);
  } catch {
    return false;
  }

  // prevent cross-origin redirect
  if (url.origin !== placeholderBase) {
    return false;
  }

  // prevent loop
  if (url.pathname.startsWith("/sign-in")) {
    return false;
  }

  return true;
}

/**
 * The home page is already the default landing spot, so there's no point encoding it as
 * `?redirect=%2F`. Next.js's `router.asPath` can report the home page as `"/?"` (a trailing,
 * empty query string) rather than a clean `"/"`, so that's normalized away before comparing.
 */
export function shouldPreserveRedirectPath(path: string | null | undefined): path is string {
  if (!isSafeInternalRedirectPath(path)) {
    return false;
  }
  return path.replace(/\?$/, "") !== "/";
}

// Module-level mailbox for the redirect path captured from Cognito's OAuth `customState`
let pendingOAuthRedirectPath: string | null = null;

export function setPendingOAuthRedirectPath(path: string | null | undefined): void {
  pendingOAuthRedirectPath = isSafeInternalRedirectPath(path) ? path : null;
}

/** Read-once: returns the pending path (if any) and clears it. */
export function consumePendingOAuthRedirectPath(): string | null {
  const path = pendingOAuthRedirectPath;
  pendingOAuthRedirectPath = null;
  return path;
}

/**
 * Non-destructive read of the pending OAuth redirect path.
 */
export function peekPendingOAuthRedirectPath(): string | null {
  return pendingOAuthRedirectPath;
}

export function resolvePostLoginRedirectPath({
  queryRedirect,
  oauthRedirect,
}: {
  queryRedirect?: string | null;
  oauthRedirect?: string | null;
}): string | null {
  if (isSafeInternalRedirectPath(oauthRedirect)) {
    return oauthRedirect;
  }
  if (isSafeInternalRedirectPath(queryRedirect)) {
    return queryRedirect;
  }
  return null;
}

/**
 * App routes are shaped as `/<practiceId>/bills/...`, so a post-login redirect target already
 * tells us which practice the user is heading to. Returns that practice id when the first path
 * segment is a UUID, otherwise `null`.
 */
export function extractPracticeIdFromPath(path: string | null | undefined): string | null {
  if (!isSafeInternalRedirectPath(path)) {
    return null;
  }

  const [firstSegment] = path.slice(1).split(/[/?#]/);
  return practiceIdPathSegmentRegex.test(firstSegment) ? firstSegment.toLowerCase() : null;
}

/**
 * Builds the Cognito `customState` value. Stays a plain URL string (not JSON) because the
 * external Vercel-preview `identity/cognito-redirect` proxy expects `customState` to be a URL it
 * can use for the origin-only bounce-back, passing the rest of the string through unchanged.
 */
export function encodeOAuthCustomState(
  origin: string | null | undefined,
  redirectPath?: string | null
): string {
  const safeOrigin = origin ?? "";
  return isSafeInternalRedirectPath(redirectPath) ? `${safeOrigin}${redirectPath}` : safeOrigin;
}

export function decodeOAuthCustomState(customState: string | null | undefined): {
  origin: string | null;
  redirectPath: string | null;
} {
  if (!customState) {
    return { origin: null, redirectPath: null };
  }
  try {
    const url = new URL(customState);
    const path = `${url.pathname}${url.search}`;
    return {
      origin: url.origin,
      redirectPath: shouldPreserveRedirectPath(path) ? path : null,
    };
  } catch {
    // Not a parseable URL - tolerate it rather than throwing, matching the previous
    // bare-origin-string behavior for any legacy/unexpected values.
    return { origin: customState, redirectPath: null };
  }
}
